import { UserCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { TooltipWrapper } from 'enrollment/components/Shared/TooltipWrapper';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import {
  useEnrollmentFamilyQuery,
  useEnrollmentProfileQuery,
} from 'enrollment/queries';
import { AvatarBoxStyled } from '../CoreBenefitCardStyled';
import { CoreBenefitCardProps } from '../../types/dtos';

export const CoreBenefitCardAvatars = ({ benefit }: CoreBenefitCardProps) => {
  const { t } = useTranslation('pages.home');
  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
  const { dependentsData } = useEnrollmentFamilyQuery({ lumosEnabled: true });

  const subscriberProfile = subscriber?.data?.subscriber_profile;
  const noSubscriberProfile = Boolean(
    !subscriberProfile ||
      (subscriberProfile?.avatar === null &&
        subscriberProfile?.profile_picture === '')
  );

  const dependentProfiles = dependentsData?.data?.dependents;
  const coverage = benefit.coverage;

  const depCovLength = coverage?.req_dependents?.length || 0;

  const maxDependentsShown = 2;
  const showMoreDependentsText = depCovLength > maxDependentsShown;
  let showMoreDependentsCount = 0;

  if (showMoreDependentsText) {
    showMoreDependentsCount = depCovLength - maxDependentsShown;
  }

  return (
    <>
      <AvatarBoxStyled data-testid="subscriber">
        <div>
          {!noSubscriberProfile ? (
            displayDependentAvatar(subscriberProfile, 32)
          ) : (
            <UserCircle
              size={32}
              alt="You are covered"
              data-testid="default-avatar-subscriber"
            />
          )}
        </div>
        <div className="body2 text-center">
          {t('coreBenefits.benefitCard.avatars.you')}
        </div>
      </AvatarBoxStyled>
      {depCovLength !== 0 &&
        coverage?.req_dependents
          .slice(0, maxDependentsShown)
          .map((dependent, index) => {
            const profile = dependentProfiles?.find(
              (profile) => profile.id === dependent?.dependent_id
            )?.dependent_profile;

            return (
              <AvatarBoxStyled
                data-testid="dependents"
                key={`${coverage.org_plan_id}-dependent${index}`}
              >
                {/* grab dependent profile and display avatar from there if it exists */}
                <div>
                  {profile ? (
                    displayDependentAvatar(profile, 32)
                  ) : (
                    <UserCircle
                      size={32}
                      alt={`${dependent.dependent_name} ${t('coreBenefits.benefitCard.avatars.dependentIsCovered')}`}
                      data-testid="default-avatar-dependent"
                    />
                  )}
                </div>
                {dependent.dependent_name.split(' ')[0].length > 6 ? (
                  <TooltipWrapper
                    position="top"
                    data-testid="tool-tip"
                    tooltipContent={dependent.dependent_name.split(' ')[0]}
                  >
                    <button
                      className="truncate body2 text-center"
                      style={{ maxWidth: '60px' }}
                    >
                      {dependent.dependent_name.split(' ')[0]}
                    </button>
                  </TooltipWrapper>
                ) : (
                  <div className="body2 text-center">
                    {dependent.dependent_name.split(' ')[0]}
                  </div>
                )}
              </AvatarBoxStyled>
            );
          })}

      {showMoreDependentsCount !== 0 && (
        <div className="d-flex align-items-center">
          <div
            data-testid="show-more-deps-count"
            style={{
              fontFamily: 'Inter',
              fontSize: '1.4rem',
              fontWeight: '500',
              letterSpacing: '.15px',
              textAlign: 'center',
              width: '32px',
            }}
          >
            {`+${showMoreDependentsCount}`}
          </div>
        </div>
      )}
    </>
  );
};
